/**
 * TextInput — an on-screen-friendly text field. Tapping it focuses a real (hidden)
 * DOM <input>, so the OS keyboard pops on phones and IME/paste/autocorrect all work;
 * the Phaser surface just renders the value + caret in the cozy palette. Submit on
 * Enter or via an attached Button. Degrades gracefully when no DOM is present
 * (node/tests): it still tracks a value programmatically.
 *
 * This is the trainer-naming + jump-into-chat control. Big tap target, never
 * hover-only (cross-platform/touch HARD rule).
 */
import Phaser from 'phaser';
import { COLORS, CSS, FONT, RADIUS, SPACING, TOUCH_TARGET } from './theme.js';
import { Panel } from './Panel.js';
import { makeClickable } from './clickable.js';
import {
  restoreGameKeyboard,
  suppressGameKeyboard,
  type KeyboardSuppression,
} from './keyboardGate.js';

export interface TextInputOptions {
  readonly width: number;
  readonly height?: number;
  readonly placeholder?: string;
  readonly maxLength?: number;
  readonly fontSize?: number;
  /** Mask the value (•••) on the Phaser surface + use a DOM password input. */
  readonly password?: boolean;
  readonly onChange?: (value: string) => void;
  readonly onSubmit?: (value: string) => void;
}

export class TextInput extends Phaser.GameObjects.Container {
  private readonly panel: Panel;
  private readonly textObj: Phaser.GameObjects.Text;
  private iWidth: number;
  private iHeight: number;
  private readonly placeholder: string;
  private readonly maxLength: number;
  private readonly isPassword: boolean;
  private readonly onChange?: (value: string) => void;
  private readonly onSubmit?: (value: string) => void;

  private value = '';
  private focused = false;
  private domInput: HTMLInputElement | null = null;
  /** Set while the game keyboard is suppressed for typing; holds the restore snapshot. */
  private keyboardSuppression: KeyboardSuppression | null = null;

  constructor(scene: Phaser.Scene, x: number, y: number, options: TextInputOptions) {
    super(scene, x, y);
    this.iWidth = options.width;
    this.iHeight = options.height ?? TOUCH_TARGET;
    this.placeholder = options.placeholder ?? '';
    this.maxLength = options.maxLength ?? 64;
    this.isPassword = options.password ?? false;
    this.onChange = options.onChange;
    this.onSubmit = options.onSubmit;
    const fontSize = options.fontSize ?? FONT.size.md;

    this.panel = new Panel(scene, 0, 0, {
      width: this.iWidth,
      height: this.iHeight,
      fill: COLORS.surfaceAlt,
      radius: RADIUS.md,
    });
    this.textObj = scene.add
      .text(-this.iWidth / 2 + SPACING.md, 0, this.placeholder, {
        fontFamily: FONT.family,
        fontSize: `${fontSize}px`,
        color: CSS.textFaint,
        fixedWidth: this.iWidth - SPACING.md * 2,
      })
      .setOrigin(0, 0.5);

    this.add([this.panel, this.textObj]);
    // Hit area via the shared helper so the tappable region matches the drawn field even
    // once Phaser applies its displayOrigin shift. The old code set a CENTERED
    // setInteractive rect AFTER setSize(), which makes the input displayOrigin w/2,h/2 and
    // left the effective hit zone offset by ~half the box (taps near the visual left/top
    // missed). makeClickable anchors displayOrigin-aware, so we can keep setSize for
    // correct container bounds and still land the rect on the drawn field. The tap handler
    // is wired ONCE here (stable ref, context-bound) rather than through makeClickable's
    // onClick, so re-applying the hit area on resize never stacks duplicate listeners.
    this.setSize(this.iWidth, this.iHeight);
    this.applyHitArea();
    this.on(Phaser.Input.Events.POINTER_DOWN, this.focus, this);

    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
    scene.add.existing(this);
  }

  /**
   * Re-apply the (displayOrigin-aware) hit rect + 'text' cursor to match the current
   * width/height. Reuses the persistent input.hitArea on repeat calls (resize), mirroring
   * Button.applyHitArea() — the single source of truth for the tappable region.
   */
  private applyHitArea(): void {
    makeClickable(this, this.iWidth, this.iHeight, { cursor: 'text' });
  }

  /**
   * Resize the field in place (responsive layouts that fit a row/width). Re-draws the
   * panel, re-fits the value text, and re-fits the hit area so taps stay accurate.
   */
  resize(width: number, height: number = this.iHeight): this {
    this.iWidth = width;
    this.iHeight = height;
    this.setSize(width, height);
    this.panel.resize(width, height);
    this.textObj.setPosition(-width / 2 + SPACING.md, 0);
    this.textObj.setFixedSize(width - SPACING.md * 2, 0);
    this.applyHitArea();
    return this;
  }

  /** Current value. */
  getValue(): string {
    return this.value;
  }

  /** Set the value programmatically (clamped to maxLength) and re-render. */
  setValue(next: string): this {
    this.value = next.slice(0, this.maxLength);
    if (this.domInput) this.domInput.value = this.value;
    this.render();
    this.onChange?.(this.value);
    return this;
  }

  /** Focus the field — pops the OS keyboard on mobile via a hidden DOM input. */
  focus(): this {
    this.focused = true;
    this.suppressGameKeyboard();
    this.ensureDomInput();
    this.domInput?.focus();
    this.render();
    return this;
  }

  blur(): this {
    this.focused = false;
    this.domInput?.blur();
    this.restoreGameKeyboard();
    this.render();
    return this;
  }

  /**
   * While the DOM input is focused, the game must stop swallowing keystrokes. The
   * overworld registers WASD/arrows via `createCursorKeys()`/`addKey()` and the menus
   * capture Space/Enter — Phaser's keyboard plugin `preventDefault()`s those captured
   * keys, so they never reach the focused <input> (dropped letters: W/A/S/D, space,
   * enter). `suppressGameKeyboard` disables global capture (keys flow to the input) AND
   * the plugin (the player won't walk while typing). See `./keyboardGate`. Idempotent.
   */
  private suppressGameKeyboard(): void {
    if (this.keyboardSuppression) return;
    const kb = this.scene?.input?.keyboard;
    if (!kb) return;
    this.keyboardSuppression = suppressGameKeyboard(kb);
  }

  /** Re-arm the game keyboard once typing ends (blur or destroy). Idempotent. */
  private restoreGameKeyboard(): void {
    const snapshot = this.keyboardSuppression;
    if (!snapshot) return;
    this.keyboardSuppression = null;
    const kb = this.scene?.input?.keyboard;
    if (!kb) return;
    restoreGameKeyboard(kb, snapshot);
  }

  /** Submit the current value (Enter / attached send button). */
  submit(): void {
    this.onSubmit?.(this.value);
  }

  override destroy(fromScene?: boolean): void {
    this.restoreGameKeyboard();
    this.removeDomInput();
    super.destroy(fromScene);
  }

  /** Lazily create the hidden DOM input the OS keyboard binds to. */
  private ensureDomInput(): void {
    if (this.domInput || typeof document === 'undefined') return;
    const el = document.createElement('input');
    el.type = this.isPassword ? 'password' : 'text';
    el.maxLength = this.maxLength;
    el.value = this.value;
    el.setAttribute('aria-label', this.placeholder || 'text input');
    // Visually hidden but focusable (so the OS keyboard appears).
    el.style.position = 'fixed';
    el.style.opacity = '0';
    el.style.left = '0';
    el.style.top = '0';
    el.style.width = '1px';
    el.style.height = '1px';
    el.style.border = 'none';
    el.style.pointerEvents = 'none';
    el.addEventListener('input', () => {
      this.value = el.value.slice(0, this.maxLength);
      this.render();
      this.onChange?.(this.value);
    });
    el.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        this.submit();
      }
    });
    el.addEventListener('blur', () => {
      this.focused = false;
      this.restoreGameKeyboard();
      this.render();
    });
    document.body.appendChild(el);
    this.domInput = el;
  }

  private removeDomInput(): void {
    if (this.domInput?.parentNode) this.domInput.parentNode.removeChild(this.domInput);
    this.domInput = null;
  }

  private render(): void {
    const showPlaceholder = this.value.length === 0;
    const caret = this.focused ? '|' : '';
    const shown = this.isPassword ? '•'.repeat(this.value.length) : this.value;
    this.textObj.setText(showPlaceholder ? this.placeholder : shown + caret);
    this.textObj.setColor(showPlaceholder ? CSS.textFaint : CSS.text);
  }
}
